import React from "react"
import PropTypes from "prop-types"
import Analytics from "lib/Analytics"
import i18n from "lib/i18n"
import clsx from "classnames"

const FREQUENCY = { ONCE: "once", MONTHLY: "monthly" }

const FrequencyButton = ({ label, selected, onClick, disabled, roundedEdge }) => (
  <button
    type="button"
    className={clsx(
      "flex-1 cursor-pointer border border-solid border-gray-200 px-4 py-1.5 font-sans text-base font-medium tracking-wider transition-colors",
      {
        "rounded-l-sm border-r-0": roundedEdge === "left",
        "rounded-r-sm": roundedEdge === "right",
        "bg-blue-600 text-white": selected,
        "bg-white text-black hover:bg-gray-100": !selected,
      },
    )}
    disabled={disabled}
    onClick={onClick}
    aria-pressed={selected}
  >
    {label}
  </button>
)

const DonationFrequency = ({
  active = true,
  onChange = () => {},
  selected = FREQUENCY.ONCE,
  initial,
}) => {
  const handleFrequency = (frequency) => (e) => {
    e.preventDefault()
    Analytics.trackFormEvent("Donation Form", "Completed", "Frequency", frequency)
    onChange(frequency)
  }

  // This returns the order of buttons based on what the config passes in as props
  const frequencies =
    initial === FREQUENCY.MONTHLY
      ? [
          {
            frequency: FREQUENCY.MONTHLY,
            label: i18n.t("donation.frequency.monthly"),
            roundedEdge: "left",
          },
          {
            frequency: FREQUENCY.ONCE,
            label: i18n.t("donation.frequency.once"),
            roundedEdge: "right",
          },
        ]
      : [
          {
            frequency: FREQUENCY.ONCE,
            label: i18n.t("donation.frequency.once"),
            roundedEdge: "left",
          },
          {
            frequency: FREQUENCY.MONTHLY,
            label: i18n.t("donation.frequency.monthly"),
            roundedEdge: "right",
          },
        ]

  return (
    <div className="mt-4">
      <label id="frequency-label" className="text-xs font-semibold text-gray-500">
        {i18n.t("donation.label.frequency")}
      </label>
      <div
        id="frequency"
        role="radiogroup"
        aria-labelledby="frequency-label"
        className="mt-1 flex w-full overflow-hidden rounded-xs"
      >
        {frequencies.map(({ frequency, label, roundedEdge }) => (
          <FrequencyButton
            key={frequency}
            label={label}
            selected={selected === frequency}
            onClick={handleFrequency(frequency)}
            disabled={!active}
            roundedEdge={roundedEdge}
            role="radio"
            aria-checked={selected === frequency}
          />
        ))}
      </div>
    </div>
  )
}

FrequencyButton.propTypes = {
  label: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  roundedEdge: PropTypes.oneOf(["left", "right"]).isRequired,
}

DonationFrequency.propTypes = {
  active: PropTypes.bool,
  initial: PropTypes.oneOf(Object.values(FREQUENCY)),
  onChange: PropTypes.func,
  selected: PropTypes.oneOf(Object.values(FREQUENCY)),
}

export default DonationFrequency
